// ClientsTab.jsx — who gets billed, and what for.
//
// A client carries a main email — one or more addresses, every one of them a
// To: recipient of the invoice and its reminders (several allowed since
// 2026-10-14) — and a CC list of everyone else who should see it. They stay
// separate fields: who is chased and who is copied is the operator's call,
// never a matter of which address happens to come first.
//
// Projects sit under their client and are the thing time is logged against, so
// this tab is where a project has to exist before it can appear on a timesheet.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listClients, createClient, updateClient, deleteClient,
  createClientProject, updateClientProject, deleteClientProject,
} from "../../api/invoices/invoices";
import { timeZoneOptions } from "./scheduleTime";

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// One address per line, comma or semicolon. Exported so the parsing the
// operator sees in the preview is the same function the tests pin.
export function splitEmails(text) {
  return String(text || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s && s.includes('@'));
}

// The main email's addresses, de-duplicated, and the entries that are not
// addresses at all — refused on save by the server, so shown before it.
export function mainEmailPreview(text) {
  const seen = new Set();
  const valid = [];
  const invalid = [];
  for (const raw of String(text || '').split(/[\s,;]+/)) {
    const e = raw.trim().toLowerCase();
    if (!e || seen.has(e)) continue;
    seen.add(e);
    (/^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/.test(e) ? valid : invalid).push(e);
  }
  return { valid, invalid };
}

export function ccPreview(ccText, mainEmail) {
  const main = new Set(splitEmails(mainEmail));
  const seen = new Set();
  return splitEmails(ccText).filter((e) => {
    if (main.has(e) || seen.has(e)) return false;
    seen.add(e);
    return true;
  });
}

// WHICH BRAND BILLS THIS CLIENT — the one thing the list never said.
//
// A client belongs to a brand (or to nobody, company-wide), and that is what
// hands a brand to an invoice built from its hours. It was settable in the form
// and visible NOWHERE: a client filed under the wrong brand looked exactly like
// a client filed under the right one, so the mistake could only be found by
// opening every client in turn.
export function brandLabel(brands, brandId) {
  if (!brandId) return '';
  const b = (brands || []).find((x) => String(x._id || x.id) === String(brandId));
  // A brand the caller cannot see (or one since deleted) is still a real
  // filing: say the id rather than pretending the client is company-wide.
  return b ? (b.name || '') : `unknown brand (${String(brandId).slice(-6)})`;
}

// The filter's own sentinel, kept out of the id space so a brand can never
// collide with it.
export const NO_BRAND = '__none__';

export function filterClientsByBrand(clients, brandFilter) {
  if (!brandFilter) return clients || [];
  if (brandFilter === NO_BRAND) return (clients || []).filter((c) => !c.brand_id);
  return (clients || []).filter((c) => String(c.brand_id || '') === String(brandFilter));
}

// ──────────────────────────────────────────────────────── Client form modal
function ClientModal({ brands, client, onClose, onSaved }) {
  const editing = !!client;
  const [name, setName] = useState(client?.name || "");
  const [brandId, setBrandId] = useState(client?.brand_id || "");
  const [email, setEmail] = useState(client?.email || "");
  const [ccText, setCcText] = useState((client?.ccEmails || []).join('\n'));
  const [contactName, setContactName] = useState(client?.contactName || "");
  const [address, setAddress] = useState(client?.address || "");
  const [country, setCountry] = useState(client?.country || "");
  const [currency, setCurrency] = useState(client?.currency || "USD");
  // Their clock: a trade chase starts at 09:00 on the due date in this zone.
  const [timeZone, setTimeZone] = useState(client?.timeZone || "");
  const [hourlyRate, setHourlyRate] = useState(client?.hourlyRate || 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const to = useMemo(() => mainEmailPreview(email), [email]);
  const cc = useMemo(() => ccPreview(ccText, email), [ccText, email]);
  const dropped = useMemo(() => splitEmails(ccText).length - cc.length, [ccText, cc]);

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    if (to.invalid.length) { setError(`Not an email address: ${to.invalid.join(', ')}`); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(), email: to.valid.join(', '), ccEmails: cc,
        contactName, address, country, currency, hourlyRate: Number(hourlyRate) || 0,
        timeZone,
      };
      if (editing) await updateClient(client._id, { ...body, brandId: brandId || null });
      else await createClient({ ...body, brandId: brandId || null });
      onSaved();
    } catch (e) { setError((e && e.message) || "Could not save the client"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={editing ? `Edit ${client.name}` : "New client"} onClose={onClose}>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      <label style={{ fontSize: 12, color: '#666' }}>Client name *</label>
      <input className="form-control mb-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Ltd" />

      {/* Editable after creation too — a client filed under the wrong brand at
          creation is a mistake worth correcting, not worth deleting a client
          and its whole billing history over. */}
      <label style={{ fontSize: 12, color: '#666' }}>Brand — which brand bills this client</label>
      <select className="form-control mb-1" aria-label="Client brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
        <option value="">Company-wide (no brand)</option>
        {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
      </select>
      {/* WHAT MOVES AND WHAT DOES NOT.
          Invoices carry their own brand_id, copied when they were written,
          because an invoice's brand is WHO BILLED — a fact about the past.
          Rewriting it to follow the client would move money between brands in
          the payout figures and change what was already sent. Only future work
          follows the client. */}
      {editing && String(client.brand_id || '') !== String(brandId || '') && (
        <div className="alert alert-info" style={{ fontSize: 12, padding: '6px 10px' }}>
          Moving <strong>{client.name}</strong> from{' '}
          <strong>{brandLabel(brands, client.brand_id) || 'company-wide'}</strong> to{' '}
          <strong>{brandLabel(brands, brandId) || 'company-wide'}</strong>.
          Invoices already written keep the brand that billed them — only new
          ones follow the client.
        </div>
      )}
      {!editing && <div style={{ height: 8 }} />}

      <label style={{ fontSize: 12, color: '#666' }}>Main email — where invoices and reminders are sent (several allowed, comma-separated)</label>
      <input className="form-control mb-1" aria-label="Main email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="accounts@acme.com, ap@acme.com" />
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        {to.valid.length === 0 ? 'No main email.' : `To: ${to.valid.join(', ')}`}
        {to.invalid.length > 0 && <span style={{ color: '#a15c00' }}>{` — not an email address: ${to.invalid.join(', ')}`}</span>}
      </div>

      <label style={{ fontSize: 12, color: '#666' }}>CC emails — one per line</label>
      <textarea className="form-control mb-1" rows={3} value={ccText} onChange={(e) => setCcText(e.target.value)}
        placeholder={"cfo@acme.com\nops@acme.com"} />
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        {cc.length === 0 ? 'No CCs.' : `${cc.length} CC${cc.length === 1 ? '' : 's'}: ${cc.join(', ')}`}
        {dropped > 0 && <span style={{ color: '#a15c00' }}>{` — ${dropped} ignored (duplicate, or already a main email)`}</span>}
      </div>

      <label style={{ fontSize: 12, color: '#666' }}>Contact name</label>
      <input className="form-control mb-2" value={contactName} onChange={(e) => setContactName(e.target.value)} />

      <label style={{ fontSize: 12, color: '#666' }}>Address</label>
      <textarea className="form-control mb-2" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Country</label>
          <input className="form-control mb-2" value={country} onChange={(e) => setCountry(e.target.value)} />
        </div>
        <div style={{ width: 90 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Currency</label>
          <input className="form-control mb-2" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </div>
        <div style={{ width: 110 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Hourly rate</label>
          <input type="number" min="0" className="form-control mb-2" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} />
        </div>
      </div>

      <label style={{ fontSize: 12, color: '#666' }}>Timezone — reminders are timed on the client's clock</label>
      <select className="form-control mb-1" aria-label="Client timezone" value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
        <option value="">Not set — uses the timezone of whoever writes the invoice</option>
        {timeZoneOptions(timeZone).map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
      </select>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        A trade contractor chase starts at 9:00 AM on the due date in this timezone.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save client"}</button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────── Project form modal
function ProjectModal({ client, project, onClose, onSaved }) {
  const editing = !!project;
  const [name, setName] = useState(project?.name || "");
  const [code, setCode] = useState(project?.code || "");
  const [description, setDescription] = useState(project?.description || "");
  const [billable, setBillable] = useState(project ? project.billable !== false : true);
  const [hourlyRate, setHourlyRate] = useState(project?.hourlyRate || 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), code, description, billable, hourlyRate: Number(hourlyRate) || 0 };
      if (editing) await updateClientProject(project._id, body);
      else await createClientProject(client._id, body);
      onSaved();
    } catch (e) { setError((e && e.message) || "Could not save the project"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={editing ? `Edit ${project.name}` : `New project for ${client.name}`} onClose={onClose}>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}
      <label style={{ fontSize: 12, color: '#666' }}>Project name *</label>
      <input className="form-control mb-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Website rebuild" />

      <label style={{ fontSize: 12, color: '#666' }}>Short code — shown on the timesheet row</label>
      <input className="form-control mb-2" value={code} onChange={(e) => setCode(e.target.value)} placeholder="WEB-26" />

      <label style={{ fontSize: 12, color: '#666' }}>Description</label>
      <textarea className="form-control mb-2" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable
        </label>
        <div style={{ width: 140 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Hourly rate</label>
          <input type="number" min="0" className="form-control" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} />
          <div style={{ fontSize: 11, color: '#888' }}>0 = use the client's rate</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save project"}</button>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────── The tab
export default function ClientsTab({ can, brands }) {
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({});
  const [clientModal, setClientModal] = useState(null);   // {} for new, client for edit
  const [projectModal, setProjectModal] = useState(null); // {client, project?}
  // Which brand's clients to show. '' is everyone; NO_BRAND is the ones filed
  // under nobody, which is the pile a mis-filed client usually lands in.
  const [brandFilter, setBrandFilter] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try { const r = await listClients(); setClients((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || "Failed to load clients"); setClients([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterClientsByBrand(clients, brandFilter), [clients, brandFilter]);
  const unbranded = useMemo(() => clients.filter((c) => !c.brand_id).length, [clients]);

  const removeClient = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Its projects go with it.`)) return;
    try { await deleteClient(c._id); await load(); }
    // The API refuses (409) when hours are logged against the client, and that
    // reason is worth showing verbatim — it names the number of entries.
    catch (e) { setErr((e && e.message) || "Could not delete the client"); }
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Delete project ${p.name}?`)) return;
    try { await deleteClientProject(p._id); await load(); }
    catch (e) { setErr((e && e.message) || "Could not delete the project"); }
  };

  const archiveProject = async (p) => {
    try { await updateClientProject(p._id, { status: p.status === 'archived' ? 'active' : 'archived' }); await load(); }
    catch (e) { setErr((e && e.message) || "Could not change the project"); }
  };

  return (
    <div>
      <style>{`
        .cl-row { border-bottom: 1px solid #f1f1f1; padding: 10px 4px; }
        .cl-name { font-weight: 600; font-size: 14px; }
        .cl-meta { font-size: 12px; color: #777; }
        .cl-cc { font-size: 11px; color: #999; }
        .cl-projects { margin: 8px 0 4px 18px; }
        .cl-project { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 3px 0; }
        .cl-archived { color: #999; text-decoration: line-through; }
        .cl-brand { background: #eef2f6; color: #3c4a54; border-radius: 10px; padding: 1px 8px; font-size: 11px; }
        .cl-nobrand { background: #fdf3e3; color: #8a5a00; }
      `}</style>

      <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 10 }}>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <label style={{ fontSize: 12, color: '#666', margin: 0 }}>Brand</label>
          <select
            className="form-control"
            style={{ width: 240, fontSize: 13 }}
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <option value="">All brands ({clients.length})</option>
            {/* Its own entry rather than a blank: a client under no brand cannot
                hand one to an invoice built from its hours, so this pile is the
                one worth being able to pull up on purpose. */}
            <option value={NO_BRAND}>Company-wide — no brand ({unbranded})</option>
            {(brands || []).map((b) => {
              const id = String(b._id || b.id);
              const n = clients.filter((c) => String(c.brand_id || '') === id).length;
              return <option key={id} value={id}>{b.name} ({n})</option>;
            })}
          </select>
          {brandFilter && (
            <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setBrandFilter('')}>Clear</button>
          )}
        </div>
        {can('CLIENT_ADD') && (
          <button className="acc-btn acc-btn-primary" onClick={() => setClientModal({})}>+ New client</button>
        )}
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {busy && clients.length === 0 && <div className="cm-empty">Loading…</div>}
      {!busy && clients.length === 0 && (
        <div className="cm-empty">No clients yet. Add one, then give it a project so time can be logged against it.</div>
      )}
      {!busy && clients.length > 0 && shown.length === 0 && (
        <div className="cm-empty">No clients under that brand.</div>
      )}

      {shown.map((c) => {
        const open = !!expanded[c._id];
        const projects = c.projects || [];
        return (
          <div className="cl-row" key={c._id}>
            <div className="d-flex align-items-baseline justify-content-between">
              <div style={{ minWidth: 0 }}>
                <span className="cl-name">{c.name}</span>
                {c.active === false && <span className="cl-meta"> · inactive</span>}
                <div className="cl-meta">
                  {/* The brand comes FIRST: it is the field that decides which
                      brand's invoice this client can be billed on, and it was
                      the one thing this row never said. */}
                  {c.brand_id
                    ? <span className="cl-brand">{brandLabel(brands, c.brand_id)}</span>
                    : <span className="cl-brand cl-nobrand" title="Not filed under any brand — an invoice built from this client's hours has no brand to inherit">company-wide</span>}
                  {' · '}
                  {c.email || <em>no main email</em>}
                  {c.hourlyRate ? ` · ${c.currency || 'USD'} ${c.hourlyRate}/h` : ''}
                  {` · ${projects.length} project${projects.length === 1 ? '' : 's'}`}
                </div>
                {(c.ccEmails || []).length > 0 && (
                  <div className="cl-cc">cc: {c.ccEmails.join(', ')}</div>
                )}
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                  onClick={() => setExpanded((p) => ({ ...p, [c._id]: !open }))}>
                  {open ? 'Hide projects' : 'Projects'}
                </button>
                {can('CLIENT_EDIT') && (
                  <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                    onClick={() => setClientModal(c)}>Edit</button>
                )}
                {can('CLIENT_DELETE') && (
                  <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                    onClick={() => removeClient(c)}>Delete</button>
                )}
              </div>
            </div>

            {open && (
              <div className="cl-projects">
                {projects.length === 0 && (
                  <div className="cl-meta">No projects yet — time cannot be logged for this client until one exists.</div>
                )}
                {projects.map((p) => (
                  <div className="cl-project" key={p._id}>
                    <span className={p.status === 'archived' ? 'cl-archived' : ''}>
                      {p.name}{p.code ? ` (${p.code})` : ''}
                    </span>
                    <span className="cl-meta">
                      {p.billable === false ? 'non-billable' : (p.hourlyRate ? `${p.hourlyRate}/h` : 'client rate')}
                    </span>
                    <span style={{ flex: 1 }} />
                    {can('CLIENT_EDIT') && (
                      <>
                        <button className="acc-btn" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => setProjectModal({ client: c, project: p })}>Edit</button>
                        <button className="acc-btn" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => archiveProject(p)}>
                          {p.status === 'archived' ? 'Restore' : 'Archive'}
                        </button>
                      </>
                    )}
                    {can('CLIENT_DELETE') && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => removeProject(p)}>Delete</button>
                    )}
                  </div>
                ))}
                {can('CLIENT_ADD') && (
                  <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginTop: 6 }}
                    onClick={() => setProjectModal({ client: c })}>+ Add project</button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {clientModal && (
        <ClientModal
          brands={brands}
          client={clientModal._id ? clientModal : null}
          onClose={() => setClientModal(null)}
          onSaved={() => { setClientModal(null); load(); }}
        />
      )}
      {projectModal && (
        <ProjectModal
          client={projectModal.client}
          project={projectModal.project}
          onClose={() => setProjectModal(null)}
          onSaved={() => { setProjectModal(null); load(); }}
        />
      )}
    </div>
  );
}
